import { ChartSettingsT } from 'business/definitions';
import {
    removePaginationFromLegend,
    getLegendWidth,
} from 'helpers/chart/legendHelpers';
import { capitalizeAndKeepTheRest } from 'helpers/helpers';

export const getPieGraphLegendSettings = ({
    isExportModeActivated,
    legendData,
    legendPosition,
    showLegend,
    showLegendOnRightSide,
    width,
}: {
    isExportModeActivated: boolean;
    legendData: { name: string }[];
    legendPosition: ChartSettingsT['legendPosition'];
    showLegend: ChartSettingsT['showLegend'];
    showLegendOnRightSide: ChartSettingsT['showLegendOnRightSide'];
    width: number;
}) => {
    const rightSideLegendSettings = {
        orient: 'vertical',
        right: 2,
        left: 'auto',
        top: 'center',
        tooltip: {
            show: true,
            position: 'bottom',
        },
        textStyle: {
            width: getLegendWidth(width),
            overflow: 'truncate',
            lineHeight: 16,
        },
    };
    return {
        orient: 'horizontal',
        left: 'center',
        right: 'auto',
        top: 'auto',
        show: showLegend,
        itemWidth: 12,
        itemHeight: 12,
        type: 'scroll',
        ...(isExportModeActivated ? removePaginationFromLegend() : {}),
        formatter(name: string) {
            return capitalizeAndKeepTheRest(name);
        },
        data: legendData,
        ...legendPosition,
        ...(showLegendOnRightSide && rightSideLegendSettings),
    };
};

/**
 * Aims to make the pie chart take as much available space as possible when the legend isn't displayed.
 * @param radius - Initial radius
 * @param height - Height of the chart
 * @param width  - Width of the chart
 * @param areLabelsShownOutside - If the labels are displayed outside the chart or not
 * @returns The position of the Pie Chart using the center an radius attributes
 */
const getPieChartCenterAndRadiusWithoutLegend = (
    radius: string[] | undefined,
    height: number,
    width: number,
    areLabelsShownOutside: boolean | undefined,
) => {
    /**
     * Legend on right side - Minimum value between the height minus some padding
     * and the width minus some padding. This value is then multiplied by 0.5 to
     * get the radius then this value is minored by 60 if labels are displayed
     * outside of the chart.
     * If the final value is negative, we fix it to 5 to avoid errors.
     */
    const pieRadiusNoLegend =
        Math.min(height - 60, width - 40) * 0.5 -
        (areLabelsShownOutside ? 60 : 0);

    return {
        center: ['50%', '50%'],
        radius: [
            radius ? radius[0] : 0,
            pieRadiusNoLegend > 0 ? pieRadiusNoLegend : 5,
        ],
    };
};

/**
 * Aims to make the pie chart take as much available space as possible considering if the legend is displayed,
 * the position of the legend, some padding and if labels are displayed outside of the chart.
 * @param showLegend - If the legend is displayed or not
 * @param showLegendOnRightSide - If the legend is on the right side or on the top of the chart
 * @param radius - The initial radius
 * @param height - The height of the chart container
 * @param width - The width of the chart container
 * @param areLabelsShownOutside - If the labels are displayed outside the chart or not
 * @returns The position of the Pie Chart using the center an radius attributes
 */
export const getPieChartCenterAndRadius = (
    showLegend: boolean | undefined,
    showLegendOnRightSide: boolean | undefined,
    radius: string[] | undefined,
    height: number,
    width: number,
    areLabelsShownOutside: boolean | undefined,
) => {
    if (!showLegend)
        return getPieChartCenterAndRadiusWithoutLegend(
            radius,
            height,
            width,
            areLabelsShownOutside,
        );

    /**
     * Legend on right side - Minimum value between the height minus some padding
     * and the width minus some padding plus the width of the legend. This value
     * is then multiplied by 0.5 to get the radius then this value is minored by
     * 60 if labels are displayed outside of the chart.
     * If the final value is negative, we fix it to 5 to avoid errors.
     */
    const pieRadiusLegendOnRightSide =
        Math.min(height - 60, width - (90 + getLegendWidth(width))) * 0.5 -
        (areLabelsShownOutside ? 60 : 0);

    /**
     * Legend on top - Minimum value between the height minus some padding plus the
     * legend height (60 + 20) and the width. This value is then multiplied by 0.5
     * to get the radius then this value is minored by 60 if labels are displayed
     * outside of the chart.
     * If the final value is negative, we fix it to 5 to avoid errors.
     */
    const pieRadius =
        Math.min(height - 80, width) * 0.5 - (areLabelsShownOutside ? 60 : 0);

    return showLegendOnRightSide
        ? {
              center: ['50%', '50%'],
              radius: [
                  radius ? radius[0] : 0,
                  pieRadiusLegendOnRightSide > 0
                      ? pieRadiusLegendOnRightSide
                      : 5,
              ],
          }
        : {
              center: ['50%', '50%'],
              radius: [radius ? radius[0] : 0, pieRadius > 0 ? pieRadius : 5],
          };
};
